// Filters

import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDarkTheme } from "@/contexts/DarkTheme";
import { Separator } from "@/components/ui/separator";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Range } from "react-range";
import { Button } from "@/components/ui/button";
import Image from "next/image";
import Link from "next/link";
import { FaCheck } from "react-icons/fa6";
import Loader from "@/components/Loader";

import { IoMdClose } from "react-icons/io";
import { LuChevronsUpDown } from "react-icons/lu";
import { useTranslation } from "react-i18next";
import {
  useGetInstrumentSuggestion,
  useGetTrendingSymbols,
} from "@/api/symbolApi";
import { useUserContext } from "@/contexts/userContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Fuse from "fuse.js";
import { CURRENT_DEFAULT_SYMBOL } from "@/config/logos";
const Filters = ({ filters, setFilters, symbol, currentPath }: any) => {
  const { darkTheme } = useDarkTheme();
  const { t } = useTranslation("marketplace");
  const [rangeTP, setRangeTP] = useState<[number, number]>([0.5, 10]); // Initial range values
  const [rangeSL, setRangeSL] = useState<[number, number]>([0.5, 10]); // Initial range values
  const [winningPercentage, setWinningPercentage] = useState<number>(0); // Initial range values
  const [copiersCountSlider, setCopiersCountSlider] = useState<number>(0); // Initial range values
  const [isAnyFilterFilled, setIsAnyFilterFilled] = useState(false);
  const [selectedTab, setSelectedTab] = useState("forex");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [searchInputValueByName, setSearchInputValueByName] = useState("");
  const [showSuggestion, setShowSuggestion]: any = useState({
    equity: true,
    crypto: true,
    forex: true,
  });
  const [trendingSymbols, setTrendingSymbols] = useState<
    { name: string; symbol: string; short_name: string }[]
  >([]);
  const [selectedSymbol, setSelectedSymbol] = useState("");
  const [showPopover, setShowPopover] = useState(false);
  const { user } = useUserContext();
  const [isParamSymbol, setIsParamSymbol] = useState(false);
  const [filteredSymbolData, setFilteredSymbolData] = useState<
    { name: string; symbol: string; short_name: string }[]
  >([]);
  const router = useRouter();
  const { slug } = router.query;
  const [suggestionsData, setSuggestionsData] = useState<
    { name: string; symbol: string; short_name: string }[]
  >([]);
  const {
    data: instrumentSuggestionData,
    isSuccess: isInstrumentSuggestionSuccess,
    isLoading: isInstrumentSuggestionLoading,
  } = useGetInstrumentSuggestion(user?.user_id);

  const {
    data: trendingSymbolsData,
    isSuccess: isTrendingSymbolSuccess,
    isLoading: isTrendingSymbolLoading,
  } = useGetTrendingSymbols("forex");

  const handleChange = (name: string, value: string) => {
    setFilters((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSymbolClick = () => {
    // setCurrentPath("");
    router.push("/marketplace");
  };

  useEffect(() => {
    if (symbol?.split("/")[2]) {
      setSelectedSymbol(symbol?.split("/")[2]);
    } else {
      setSelectedSymbol("");
    }
  }, [symbol]);

  useEffect(() => {
    if (searchInputValueByName) {
      handleChange("strategy_name", searchInputValueByName);
    } else {
      handleChange("strategy_name", "");
    }
  }, [searchInputValueByName]);

  useEffect(() => {
    if (Array.isArray(slug) && slug.length > 0) {
      setIsParamSymbol(true);
    } else {
      setIsParamSymbol(false);
    }
  }, [slug]);

  useEffect(() => {
    if (isInstrumentSuggestionSuccess && instrumentSuggestionData) {
      if (Array.isArray(instrumentSuggestionData.data)) {
        setTrendingSymbols([...instrumentSuggestionData.data]);
      }
    }
  }, [instrumentSuggestionData]);

  useEffect(() => {
    if (isTrendingSymbolSuccess && trendingSymbolsData) {
      setSuggestionsData(
        Object.entries(
          trendingSymbolsData.data as { [key: string]: string }
        ).map(([symbol, name], index: number) => {
          return { name, symbol, short_name: symbol };
        })
      );
    }
  }, [isTrendingSymbolSuccess, trendingSymbolsData, isTrendingSymbolLoading]);

  const STEP = 1;
  const MIN = 0;
  const MAX = 10;

  let circleStyles = {
    height: "13px",
    width: "13px",
    borderRadius: "50%",
    border: "2px solid black",
    backgroundColor: "#fff",
    // backgroundColor: isDragged ? "#0a84ff" : "#999",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  };
  let lineStyling = {
    height: "5px",
    width: "96%",
    margin: "0 auto",
    borderRadius: "3px",
  };
  let clearFilters = () => {
    setRangeTP([0, 10]);
    setRangeSL([0, 10]);
    setWinningPercentage(0);
    setCopiersCountSlider(0);
    setSelectedSymbol("");
    setFilters({
      sort_order: "",
      win_p: "",
      SR: "",
      copiers: "",
      timeframe: "",
      sl_range: "",
      tp_range: "",
      exit: "",
      action: "",
      indicator_category: "",
      asset_class: "",
      symbol: "",
    });
  };

  useEffect(() => {
    const checkForFilters = Object.values(filters).some(
      (value) => value !== ""
    );
    setIsAnyFilterFilled(checkForFilters);
  }, [filters]);

  const fuse = useMemo(() => {
    if (trendingSymbols.length === 0) return null;

    return new Fuse(trendingSymbols, {
      keys: ["name", "symbol", "short_name"],
      threshold: 0.4,
    });
  }, [trendingSymbols, searchInputValue]);

  useEffect(() => {
    if (!fuse) {
      return;
    }
    console.log("searching for");
    if (searchInputValue.trim()) {
      const results = fuse.search(searchInputValue);
      setFilteredSymbolData(results.map((res) => res.item));
    } else {
      setFilteredSymbolData([]);
    }
  }, [searchInputValue, fuse, trendingSymbols]);

  return (
    <>
      <Card className=" h-auto  ">
        <CardHeader className="text-xl font-bold flex flex-row justify-between items-center">
          <div className="w-fit">{t("Filters")}</div>

          {!isParamSymbol && isAnyFilterFilled && (
            <Image
              width={26}
              height={26}
              src="/images/marketplace/cross.png"
              alt="filter image"
              className={`mr-2 cursor-pointer ${
                darkTheme ? "" : "marketplace-filter-icon-dark"
              }`}
              onClick={clearFilters}
            />
          )}
        </CardHeader>
        <CardContent>
          <Label>Search by name</Label>
          <Input
            className="mt-2"
            value={searchInputValueByName}
            onChange={(e) => setSearchInputValueByName(e.target.value)}
          ></Input>
        </CardContent>
        <CardContent>
          <div className="mb-2">
            {t("Win Percentage")} ({winningPercentage}%)
          </div>

          <Range
            step={STEP}
            min={MIN}
            max={100}
            values={[winningPercentage]}
            onChange={(values) => {
              setWinningPercentage(values[0]);
              handleChange("win_p", String(values[0]));
            }}
            renderTrack={({ props, children }) => {
              const percent = ((winningPercentage - MIN) / (100 - MIN)) * 100;
              return (
                <div
                  {...props}
                  style={{
                    ...props.style,
                    ...lineStyling,
                    background: `linear-gradient(to right, hsl(var(--primary)) 0%, hsl(var(--primary)) ${percent}%, hsl(var(--muted)) ${percent}%, hsl(var(--muted)) 100%)`,
                  }}
                >
                  {children}
                </div>
              );
            }}
            renderThumb={({ props, isDragged }) => {
              const { key, ...restProps } = props;
              return (
                <div
                  key={key}
                  {...restProps}
                  style={{
                    ...props.style,
                    ...circleStyles,
                  }}
                />
              );
            }}
          />
        </CardContent>
        <CardContent>
          <div className="mb-2">
            {t("Copiers")} ({copiersCountSlider})
          </div>

          <Range
            step={STEP}
            min={MIN}
            max={MAX}
            values={[copiersCountSlider]}
            onChange={(values) => {
              setCopiersCountSlider(values[0]);
              handleChange("copiers", String(values[0]));
            }}
            renderTrack={({ props, children }) => {
              const percent = ((copiersCountSlider - MIN) / (MAX - MIN)) * 100;

              return (
                <div
                  {...props}
                  style={{
                    ...props.style,
                    ...lineStyling,
                    //   background: `linear-gradient(to right, #000 0%, #000 ${percent}%, #ccc ${percent}%, #ccc 100%)`,
                    background: `linear-gradient(to right, hsl(var(--primary)) 0%, hsl(var(--primary)) ${percent}%, hsl(var(--muted)) ${percent}%, hsl(var(--muted)) 100%)`,
                  }}
                >
                  {children}
                </div>
              );
            }}
            renderThumb={({ props, isDragged }) => {
              const { key, ...restProps } = props;

              return (
                <div
                  key={key}
                  {...restProps}
                  style={{
                    ...props.style,
                    ...circleStyles,
                  }}
                />
              );
            }}
          />
        </CardContent>
        <CardContent>
          <div className="mb-2">{t("Sharpe Ratio")}</div>
          <Select
            value={filters.SR}
            onValueChange={(value) => handleChange("SR", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value={"Good"}>Good</SelectItem>
                <SelectItem value={"Excellent"}>Excellent</SelectItem>
                <SelectItem value={"Poor"}>Poor</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </CardContent>

        <CardContent>
          <div className="mb-2">{t("Timeframe")}</div>
          <Select
            value={filters.timeframe}
            onValueChange={(value) => handleChange("timeframe", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value={"3m"}>3m</SelectItem>
                <SelectItem value={"5m"}>5m</SelectItem>
                <SelectItem value={"15m"}>15m</SelectItem>
                <SelectItem value={"30m"}>30m</SelectItem>
                {/* <SelectItem value={"45m"}>45m</SelectItem> */}
                <SelectItem value={"69m"}>60m</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </CardContent>
        <CardContent className="relative">
          <div className="mb-2">{t("Symbol Search")}</div>

          <Popover
            open={showPopover}
            onOpenChange={(open) => setShowPopover(open)}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className={cn(
                  "w-full justify-between",
                  "text-muted-foreground"
                )}
                disabled={isParamSymbol}
                // onClick={() => setShowPopover(true)}
              >
                {!selectedSymbol ? "Search for an asset" : selectedSymbol}
                <div className="flex flex-row items-center">
                  {selectedSymbol ? (
                    <>
                      {" "}
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedSymbol("");
                          handleChange("symbol", "");
                          if (currentPath !== "/marketplace") {
                            handleSymbolClick();
                          }
                        }}
                      >
                        <IoMdClose />
                      </Button>
                      <Separator orientation="vertical" />
                    </>
                  ) : (
                    <></>
                  )}

                  <LuChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </div>
              </Button>
            </PopoverTrigger>
            <PopoverContent>
              <div>
                <input
                  className="bg-transparent  outline-none text-xs w-full p-3 border rounded-lg"
                  onChange={(srch) => {
                    console.log("searching for", srch.target.value);
                    setSearchInputValue(srch.target.value);
                  }}
                  value={searchInputValue}
                  placeholder={`Example: Start typing GBP...${CURRENT_DEFAULT_SYMBOL}`}
                />

                <div
                  className={`max-h-72 min-h-[100px] flex flex-col rounded-md border overflow-y-auto`}
                >
                  {filteredSymbolData.length > 0
                    ? filteredSymbolData.map((item, index) => {
                        let name =
                          item.name.length > 50
                            ? item.name.slice(0, 50) + "..."
                            : item.name;
                        return (
                          <div
                            key={item.symbol + "_" + index}
                            onClick={() => {
                              setSelectedSymbol(item.symbol);
                              handleChange("symbol", item.symbol);
                              setShowPopover(false);
                            }}
                          >
                            <div className="flex flex-row w-full items-center hover:bg-[hsl(var(--background))] rounded-sm pr-3 pl-2">
                              <div className="">
                                <FaCheck
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    item.symbol === selectedSymbol
                                      ? "opacity-100"
                                      : "opacity-0"
                                  )}
                                />
                              </div>
                              <div className="flex flex-col text-sm cursor-pointer w-full pt-2 pb-2">
                                <div className="border-b mb-2 w-fit">
                                  {item.symbol}
                                </div>
                                {/* <Separator /> */}
                                <div
                                  title={item.name}
                                  className="text-muted-foreground text-xs"
                                >
                                  {name}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    : searchInputValue && (
                        <div className="w-full flex justify-center p-4 text-muted-foreground text-xs">
                          No symbol found
                        </div>
                      )}
                  {/* suggestion */}
                  {filteredSymbolData.length == 0 && (
                    <div>
                      <div className="text-center mt-4 mb-2 text-sm text-muted-foreground">
                        Top Trending
                      </div>
                      {suggestionsData.map((item: any, index: number) => {
                        let name =
                          item.name.length > 50
                            ? item.name.slice(0, 50) + "..."
                            : item.name;
                        return (
                          <div
                            key={item.symbol + "_" + index}
                            onClick={() => {
                              setSelectedSymbol(item.symbol);
                              handleChange("symbol", item.symbol);
                              setShowPopover(false);
                            }}
                          >
                            <div className="flex flex-row w-full items-center hover:bg-[hsl(var(--background))] rounded-sm pr-3 pl-2">
                              <div className="">
                                <FaCheck
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    item.symbol === selectedSymbol
                                      ? "opacity-100"
                                      : "opacity-0"
                                  )}
                                />
                              </div>
                              <div className="flex flex-col text-sm cursor-pointer w-full pt-2 pb-2">
                                <div className="border-b mb-2 w-fit">
                                  {item.symbol}
                                </div>
                                {/* <Separator /> */}
                                <div
                                  title={item.name}
                                  className="text-muted-foreground text-xs"
                                >
                                  {name}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              {/* </div> */}
            </PopoverContent>
          </Popover>
        </CardContent>
        <CardContent>
          <div className="mb-2">{t("Indicator category")}</div>
          <Select
            value={filters.indicator_category}
            onValueChange={(value) => handleChange("indicator_category", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value={"Volatility"}>Volatility</SelectItem>
                <SelectItem value={"Overlap"}>Overlap</SelectItem>
                <SelectItem value={"Momentum"}>Momentum</SelectItem>
                <SelectItem value={"Volume"}>Volume</SelectItem>
                <SelectItem value={"Trend"}>Trend</SelectItem>
                <SelectItem value={"Statistics"}>Statistics</SelectItem>
                <SelectItem value={"Cycles"}>Cycles</SelectItem>
                <SelectItem value={"High Volume"}>High Volume</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </CardContent>

        <CardContent>
          <div className="mb-2">
            {t("Stop Loss Range")} ({rangeSL[0]}% - {rangeSL[1]}%)
          </div>
          <Range
            step={STEP}
            min={0.5}
            max={MAX}
            values={rangeSL}
            onChange={(values) => {
              setRangeSL(values as [number, number]);
              handleChange("sl_range", values.join());
            }}
            renderTrack={({ props, children }) => {
              const [start, end] = rangeSL;
              const startPercent = ((start - 0.5) / (MAX - 0.5)) * 100;
              const endPercent = ((end - 0.5) / (MAX - 0.5)) * 100;
              return (
                <div
                  {...props}
                  style={{
                    ...props.style,
                    ...lineStyling,
                    background: `linear-gradient(to right,hsl(var(--muted)) 0%, hsl(var(--muted)) ${startPercent}%, hsl(var(--primary)) ${startPercent}%, hsl(var(--primary)) ${endPercent}%, hsl(var(--muted)) ${endPercent}%, hsl(var(--muted)) 100%)`,
                  }}
                >
                  {children}
                </div>
              );
            }}
            renderThumb={({ props, isDragged }) => {
              const { key, ...restProps } = props;

              return (
                <div
                  key={key}
                  {...restProps}
                  style={{
                    ...props.style,
                    ...circleStyles,
                  }}
                />
              );
            }}
          />
        </CardContent>
        <CardContent>
          <div className="mb-2">
            {t("Take Profit Range")} ({rangeTP[0]}% - {rangeTP[1]}%)
          </div>
          <Range
            step={STEP}
            min={0.5}
            max={MAX}
            values={rangeTP}
            onChange={(values) => {
              setRangeTP(values as [number, number]);
              handleChange("tp_range", values.join());
            }}
            renderTrack={({ props, children }) => {
              const [start, end] = rangeTP;
              const startPercent = ((start - 0.5) / (MAX - 0.5)) * 100;
              const endPercent = ((end - 0.5) / (MAX - 0.5)) * 100;
              return (
                <div
                  {...props}
                  style={{
                    ...props.style,
                    ...lineStyling,
                    background: `linear-gradient(to right,hsl(var(--muted)) 0%, hsl(var(--muted)) ${startPercent}%, hsl(var(--primary)) ${startPercent}%, hsl(var(--primary)) ${endPercent}%, hsl(var(--muted)) ${endPercent}%, hsl(var(--muted)) 100%)`,
                  }}
                >
                  {children}
                </div>
              );
            }}
            renderThumb={({ props, isDragged }) => {
              const { key, ...restProps } = props;

              return (
                <div
                  key={key}
                  {...restProps}
                  style={{
                    ...props.style,
                    ...circleStyles,
                  }}
                />
              );
            }}
          />
        </CardContent>

        <CardContent>
          <div className="mb-2">{t("Action")}</div>
          <Select
            value={filters.action}
            onValueChange={(value) => handleChange("action", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value={"Buy"}>Buy</SelectItem>
                <SelectItem value={"Sell"}>Sell</SelectItem>
                <SelectItem value={"Smart Buy"}>Smart Buy</SelectItem>
                <SelectItem value={"Smart Sell"}>Smart Sell</SelectItem>
                <SelectItem value={"Buy Sell"}>Buy & Sell</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>
      <Card className="mt-4 h-auto ">
        <CardHeader className="text-l  font-bold flex flex-row justify-between items-center pt-4 pb-4">
          <div className="w-fit">{t("Systematic Trading Guides")}</div>
        </CardHeader>
        <CardContent className="flex  justify-between relative flex-wrap pb-2">
          <Link
            href={
              "https://trylevel2.gitbook.io/getting-started/systematic-trading-guides/bull-market"
            }
            target="_blank"
            className="w-[47%]  h-[50px] mb-3"
          >
            <Card className="w-[100%] text-xs h-[100%] flex justify-center mb-4 cursor-pointer items-center bg-[hsl(var(--background))]">
              {t("Bull Market")}
            </Card>
          </Link>
          <Link
            className="w-[47%] h-[50px] mb-3"
            href={
              "https://trylevel2.gitbook.io/getting-started/systematic-trading-guides/bear-market"
            }
            target="_blank"
          >
            <Card className="w-[100%] text-xs h-[100%] flex justify-center mb-4 cursor-pointer items-center bg-[hsl(var(--background))]">
              {t("Bear Market")}
            </Card>
          </Link>
          <Link
            href={
              "https://trylevel2.gitbook.io/getting-started/systematic-trading-guides/neutral-market"
            }
            target="_blank"
            className="w-[47%]  h-[50px] mb-3"
          >
            <Card className="w-[100%] text-xs h-[100%] flex justify-center mb-4 cursor-pointer items-center bg-[hsl(var(--background))]">
              {t("Neutral Market")}
            </Card>
          </Link>
          <Link
            className="w-[47%]  h-[50px] mb-3"
            href={
              "https://trylevel2.gitbook.io/getting-started/systematic-trading-guides/multi-market"
            }
            target="_blank"
          >
            <Card className="w-[100%] text-xs h-[100%] flex justify-center mb-4 cursor-pointer items-center bg-[hsl(var(--background))]">
              {t("Multi-Market")}
            </Card>
          </Link>
        </CardContent>
      </Card>
    </>
  );
};

export default Filters;
